import { create } from "zustand";
import { useConfigStore } from "./";
import {
  amlbotAPI,
  type GetAmlHistoryRequest,
  type GetAmlItemByIdRequest,
  type SendPdfReportRequest,
} from "@/api";
import { type AmlHistoryItemData } from "./models";
import { mapAmlItem } from "./mappers";

interface AmlHistory {
  items: AmlHistoryItemData[];
  totalCount: number;
  pageLimit: number;
}

interface AmlHistoryStore {
  history: AmlHistory | null;
  historyLoading: boolean;
  pdfReportSubmittedIds: string[];

  // Actions
  setHistoryItems: (items: AmlHistoryItemData[]) => void;
  setTotalCount: (totalCount: number) => void;
  setPageLimit: (pageLimit: number) => void;
  fetchAmlHistory: () => Promise<void>;
  fetchAmlItemById: (itemId: string) => Promise<void>;
  sendPdfReport: (uid: string, email: string) => Promise<void>;
}

export const useAmlHistoryStore = create<AmlHistoryStore>((set, get) => ({
  history: null,
  historyLoading: false,
  pdfReportSubmittedIds: [],
  errorCode: null,
  amlError: null,

  setHistoryItems: (items: AmlHistoryItemData[]) => {
    const currentHistory = get().history;
    set({
      history: {
        items,
        totalCount: currentHistory?.totalCount ?? 0,
        pageLimit: currentHistory?.pageLimit ?? 0,
      },
    });
  },
  setTotalCount: (totalCount: number) => {
    const currentHistory = get().history;
    set({
      history: {
        items: currentHistory?.items ?? [],
        totalCount,
        pageLimit: currentHistory?.pageLimit ?? 0,
      },
    });
  },
  setPageLimit: (pageLimit: number) => {
    const currentHistory = get().history;
    set({
      history: {
        items: currentHistory?.items ?? [],
        totalCount: currentHistory?.totalCount ?? 0,
        pageLimit,
      },
    });
  },

  fetchAmlHistory: async () => {
    const { historyLoading } = get();

    if (historyLoading) {
      return;
    }

    const { appId, externalId, username } = useConfigStore.getState();

    if (!appId || !externalId || !username) {
      return;
    }

    const payload: GetAmlHistoryRequest = {
      appId,
      identifier: externalId,
      username,
    };

    try {
      set({ historyLoading: true });

      const response = await amlbotAPI.getAmlHistory(payload);

      if (response.success) {
        const items = response.data.history.map(mapAmlItem);
        set({
          history: {
            items: items,
            totalCount: response.data.totalCount,
            pageLimit: response.data.pageLimit,
          },
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      set({ historyLoading: false });
    }
  },
  fetchAmlItemById: async (itemId: string) => {
    const { appId, externalId, username } = useConfigStore.getState();

    if (!appId || !externalId || !username) {
      return;
    }

    const payload: GetAmlItemByIdRequest = {
      appId,
      identifier: externalId,
      username,
      uid: itemId,
    };

    try {
      set({ historyLoading: true });

      const response = await amlbotAPI.getAmlItemById(payload);

      if (response.success) {
        const amlItem = mapAmlItem(response.data);
        const itemIndex = get().history?.items.findIndex(
          (item) => item.uuid === amlItem.uuid,
        );

        if (itemIndex != undefined && itemIndex !== -1) {
          const currentHistory = get().history;
          const items = [...(currentHistory?.items ?? [])];
          items.splice(itemIndex, 1, amlItem);
          set({
            history: {
              items,
              totalCount: currentHistory?.totalCount ?? 0,
              pageLimit: currentHistory?.pageLimit ?? 0,
            },
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      set({ historyLoading: false });
    }
  },
  sendPdfReport: async (uid: string, email: string) => {
    const { appId, externalId, username } = useConfigStore.getState();

    if (!appId || !externalId || !username) {
      throw new Error("Widget is not initialized");
    }

    const payload: SendPdfReportRequest = {
      appId,
      identifier: externalId,
      username,
      uid,
      email,
    };

    const response = await amlbotAPI.sendPdfReport(payload);

    if (!response.success) {
      throw new Error("Failed to send PDF report");
    }

    const submitted = get().pdfReportSubmittedIds;

    if (!submitted.includes(uid)) {
      set({ pdfReportSubmittedIds: [...submitted, uid] });
    }
  },
}));
